/* Sign in with Apple, Google and Microsoft, through GoTrue.
 *
 * WHY THIS EXISTS AT ALL, WHEN THE PASSKEY WAS SUPPOSED TO MAKE IT REDUNDANT.
 * Every messaging channel we have runs through somebody else's queue: Twilio's
 * compliance profile, Qatar's sender registration, Meta's suspension review.
 * These three need no external approval, so they are not a nicety beside the
 * phone path -- for now they are the only way in we can switch on ourselves.
 *
 * PKCE, NOT IMPLICIT, AND THE REASON IS THE ADDRESS BAR.
 * GoTrue's default redirect flow hands the session back in the URL fragment.
 * That fragment is a live access token, and it lands in browser history, in
 * the back stack, in anything that reads location, and in a screenshot of the
 * address bar. PKCE hands back a single-use code instead, which is worthless
 * without the verifier this device kept and never transmitted, and the token
 * arrives in a POST response body where it belongs.
 *
 * We still READ the implicit shape on return. Not as a preference -- if a
 * server is configured for implicit and we only understood PKCE, the customer
 * would come back from Google to a screen that silently did nothing, which is
 * the exact dead end this project keeps refusing to ship. Reading both costs
 * twelve lines and removes a whole class of "it just doesn't work".
 */
import type { Session } from "./api";

/** GoTrue's own names for these, which are not all the marketing names.
 *  Microsoft is "azure" to GoTrue, and getting that wrong produces a 400 that
 *  reads like the provider is down rather than like a typo. */
export type OAuthProvider = "google" | "apple" | "azure";

export const PROVIDER_LABEL: Record<OAuthProvider, string> = {
  google: "Google",
  apple: "Apple",
  azure: "Microsoft",
};

/* The verifier has to outlive a full trip to another origin and back, and on a
 * home-screen PWA that round trip can tear down and rebuild the page. So it is
 * written to storage rather than held in a closure.
 *
 * localStorage rather than sessionStorage, deliberately: iOS opens the provider
 * in a separate context and can return to a fresh tab, where sessionStorage is
 * empty and the customer would be told their sign-in failed when it succeeded. */
const VERIFIER_KEY = "damga.oauth.verifier.v1";
const PROVIDER_KEY = "damga.oauth.provider.v1";

function put(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Private mode. Handled at the call site: no verifier means we ask the
     * server for the implicit shape instead of the PKCE one. */
  }
}

function take(key: string): string | null {
  try {
    const v = localStorage.getItem(key);
    localStorage.removeItem(key);
    return v;
  } catch {
    return null;
  }
}

function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** 43 characters of base64url from 32 random bytes: the shortest length RFC
 *  7636 permits, and the length supabase-js uses, so it is the one GoTrue is
 *  actually exercised with. */
export function newVerifier(random: (n: number) => Uint8Array = randomBytes): string {
  return base64url(random(32));
}

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

export async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/** The URL to send the browser to. Built rather than navigated so it can be
 *  asserted in a test without a browser, and so the caller decides how to
 *  navigate -- which matters on iOS, where the wrong kind of navigation from a
 *  home-screen app opens a window that never comes back. */
export async function authorizeUrl(
  authUrl: string,
  provider: OAuthProvider,
  redirectTo: string,
  verifier?: string,
): Promise<string> {
  const u = new URL(`${authUrl.replace(/\/$/, "")}/authorize`);
  u.searchParams.set("provider", provider);
  u.searchParams.set("redirect_to", redirectTo);
  if (verifier) {
    u.searchParams.set("code_challenge", await challengeFor(verifier));
    // Lowercase. GoTrue compares this string, and "S256" is rejected by the
    // versions that accept "s256".
    u.searchParams.set("code_challenge_method", "s256");
  }
  return u.toString();
}

/** Start the trip. Returns nothing, because the page is gone after this. */
export async function beginOAuth(
  authUrl: string,
  provider: OAuthProvider,
  redirectTo: string,
): Promise<void> {
  const verifier = newVerifier();
  put(VERIFIER_KEY, verifier);
  put(PROVIDER_KEY, provider);
  // If storage refused, we cannot complete PKCE on return, so we must not ask
  // for it. Asking and then failing would strand the customer after they had
  // already approved us with the provider.
  const kept = (() => {
    try {
      return localStorage.getItem(VERIFIER_KEY) === verifier;
    } catch {
      return false;
    }
  })();
  const url = await authorizeUrl(authUrl, provider, redirectTo, kept ? verifier : undefined);
  window.location.assign(url);
}

export type OAuthReturn =
  | { kind: "session"; session: Session; provider: OAuthProvider | null }
  | { kind: "error"; message: string; provider: OAuthProvider | null }
  | null;

/** True when the current URL looks like a return from a provider. Cheap, and
 *  separate from consuming it, so a caller can decide to show a spinner before
 *  doing any async work. */
export function looksLikeOAuthReturn(loc: { search: string; hash: string }): boolean {
  const q = new URLSearchParams(loc.search);
  const f = new URLSearchParams(loc.hash.replace(/^#/, ""));
  return Boolean(q.get("code") ?? q.get("error") ?? f.get("access_token") ?? f.get("error"));
}

/** Consume the return. Clears the tokens or the code out of the address bar
 *  before doing anything else with them, so a reload cannot replay it and a
 *  screenshot cannot leak it. */
export async function completeOAuth(authUrl: string): Promise<OAuthReturn> {
  const loc = window.location;
  if (!looksLikeOAuthReturn(loc)) return null;

  const q = new URLSearchParams(loc.search);
  const f = new URLSearchParams(loc.hash.replace(/^#/, ""));
  const provider = (take(PROVIDER_KEY) as OAuthProvider | null) ?? null;
  const verifier = take(VERIFIER_KEY);

  // Address bar first, always, and before any await. An exception thrown
  // between here and the end must not leave a live token on screen.
  try {
    history.replaceState(null, "", loc.pathname);
  } catch {
    /* Some embedded views refuse. Nothing else we can do, and it must not
     * stop a legitimate sign-in from completing. */
  }

  const failure = q.get("error_description") ?? q.get("error") ?? f.get("error_description") ?? f.get("error");
  if (failure) {
    // "access_denied" is the customer pressing Cancel, which is not an error
    // to shout about; the caller decides how to say it.
    return { kind: "error", message: failure, provider };
  }

  const implicit = f.get("access_token");
  if (implicit) {
    return {
      kind: "session",
      provider,
      session: {
        access_token: implicit,
        refresh_token: f.get("refresh_token") ?? "",
        expires_at: expiresAt(f.get("expires_at"), f.get("expires_in")),
      },
    };
  }

  const code = q.get("code");
  if (!code) return null;
  if (!verifier) {
    // The code is real but this device cannot prove it asked for it. Storage
    // was cleared mid-flight, or the return landed in a different browser.
    return {
      kind: "error",
      provider,
      message: "verifier_missing",
    };
  }

  let res: Response;
  try {
    res = await fetch(`${authUrl.replace(/\/$/, "")}/token?grant_type=pkce`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ auth_code: code, code_verifier: verifier }),
    });
  } catch {
    return { kind: "error", provider, message: "unreachable" };
  }
  const body = (await res.json().catch(() => ({}))) as Record<string, string>;
  if (!res.ok || !body.access_token) {
    return { kind: "error", provider, message: body.msg ?? body.error_description ?? `auth ${res.status}` };
  }
  return {
    kind: "session",
    provider,
    session: {
      access_token: body.access_token,
      refresh_token: body.refresh_token ?? "",
      expires_at: expiresAt(body.expires_at, body.expires_in),
    },
  };
}

/* GoTrue returns expires_at on some paths and only expires_in on others, and
 * an undefined expires_at is worse than a wrong one: the refresh check reads
 * `expires_at - 60 <= now`, which is NaN for undefined, which is false, so the
 * session is never refreshed and simply stops working an hour later. */
function expiresAt(at: string | number | null | undefined, inSeconds: string | number | null | undefined): number {
  const parsed = typeof at === "string" ? Number(at) : at;
  if (typeof parsed === "number" && Number.isFinite(parsed) && parsed > 0) return parsed;
  const secs = typeof inSeconds === "string" ? Number(inSeconds) : inSeconds;
  return Math.floor(Date.now() / 1000) + (Number.isFinite(secs as number) ? (secs as number) : 3600);
}

/* WHICH PROVIDERS ARE ACTUALLY SWITCHED ON.
 *
 * ASKED, NOT CONFIGURED. The obvious way to do this is a list in the app's own
 * config, and that list is a second copy of a fact the auth service already
 * holds -- so it drifts, and the way it drifts is a button that sends somebody
 * to an error page on Google's domain after they have already committed. That
 * is a worse dead end than an absent button, because it looks like our fault
 * and it happens too late to choose differently.
 *
 * GoTrue publishes the truth at /settings without authentication, so the
 * screen asks. Switching Google on becomes one Terraform change with no
 * frontend release at all.
 */
export async function enabledProviders(authUrl: string): Promise<Set<OAuthProvider>> {
  const res = await fetch(`${authUrl.replace(/\/$/, "")}/settings`);
  if (!res.ok) throw new Error(`settings ${res.status}`);
  const body = (await res.json()) as { external?: Record<string, boolean> };
  const ext = body.external ?? {};
  const live = new Set<OAuthProvider>();
  // Named one by one rather than filtered from the response, because that
  // response lists twenty-odd providers and we offer three. A loop over its
  // keys would put a Discord button on the screen the day somebody enables it.
  for (const p of ["google", "apple", "azure"] as const) if (ext[p]) live.add(p);
  return live;
}
